import { useColorImages } from "../data/useColorImages";
import React, { useEffect, useRef, useMemo, useState, memo } from "react";
import {
  Engine,
  Scene,
  Vector3,
  Mesh,
  MeshBuilder,
  StandardMaterial,
  Color3,
  Color4,
  ArcRotateCamera,
  HemisphericLight,
  DirectionalLight,
  PointLight,
  SpotLight,
  ShadowGenerator,
  Texture,
  DynamicTexture,
  ActionManager,
  Tools,
  TransformNode,
  PointerEventTypes,
  Matrix,
  Quaternion
} from "@babylonjs/core";
import { Form } from "react-bootstrap";
import { Material } from "@babylonjs/core";
import FloatingToolbar from "../modules/helpers/FloatingToolbar";
import useMillingDisplay from "./useMillingDisplay";
import { useLanguage } from "../modules/helpers/LanguageContext";
function ModelViewer({
  objects,
  createdItems,
  usedColors: catalogColors,
  onItemUpdate,
  onObjectUpdate,
  roomSize,
  onRoomSizeChange,
  hiddenItems
}) {
  // selection state
  const visibleColors = useMemo(() => {
    const ids = new Set((createdItems || []).filter(item => !hiddenItems?.has(item.itemId ?? item.tmpId)).map(item => String(item.color?.colorId)));
    return (catalogColors || []).filter(color => ids.has(String(color.colorId)));
  }, [catalogColors, createdItems, hiddenItems]);
  const usedColors = useColorImages(visibleColors);
  const { t } = useLanguage();
  const millingDisplay = useMillingDisplay();
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [objPosition, setObjPosition] = useState({ x: 0, y: 0, z: 0 }); // in mm
  const [objRotation, setObjRotation] = useState({ x: 0, y: 0, z: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [measureMode, setMeasureMode] = useState(false);
  const [firstPick, setFirstPick] = useState(null);
  const SNAP_THRESHOLD_MM = 50;
  const SNAP_THRESHOLD_ROOM_MM = 500;
  const hasPositionedCameraRef = useRef(false);
  const [isSnappingEnabled, setIsSnappingEnabled] = useState(true);
  const measureModeRef = useRef(measureMode);
  const firstPickRef = useRef(firstPick);
  const shiftSnapFirstRef = useRef(null);
  const SCALE = 0.001;
  const systemObjectIds = useRef(new Set());
  const [controlMode, setControlMode] = useState("object");
  const [transformMode, setTransformMode] = useState("move");
  const containerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastPointerPosRef = useRef(null);
  const selectedObjectIdRef = useRef(null);
  const controlModeRef = useRef(controlMode);
  const prevRoomSizeRef = useRef(roomSize);
  const dragPlanePointRef = useRef(null);
  const sizeLabelsRef = useRef({});
  const hiddenObjectIdsRef = useRef(new Set());
  const dragStartRootPosRef = useRef(null);
  const dragModeRef = useRef(null);
  const objectsRef = useRef(objects);
  const sceneOwnsTransformRef = useRef(false);
  const pointerMovedRef = useRef(false);
  const pointerDownXRef = useRef(0);
  const measureLabelMaterialRef = useRef(null);
  const beforeRenderObserverRef = useRef(null);
  const [showSizeLabels, setShowSizeLabels] = useState(true);
  const showSizeLabelsRef = useRef(showSizeLabels);
  const roomFloorRef = useRef(null);
  useEffect(() => {
    showSizeLabelsRef.current = showSizeLabels;

    Object.values(sizeLabelsRef.current).forEach((label) => {
      if (!label) return;
      label.setEnabled(showSizeLabels);
    });
  }, [showSizeLabels]);
  const snapLockRef = useRef({
    x: null,
    z: null
  });
  console.log("createdItems: ", createdItems);
  useEffect(() => {
    objectsRef.current = objects;
  }, [objects]);
  const collisionOnDropRef = useRef(false);
  const measureVisualsRef = useRef({
    p1: null,
    p1dot: null,
    p1center: null,
    p2: null,
    p2dot: null,
    p2center: null,
    line: null,
    label: null
  });
  const materialCacheRef = useRef(new Map());
  const textureCacheRef = useRef(new Map());
  useEffect(() => {
    return () => {
      clearMeasurement(sceneRef.current);
    };
  }, []);
  useEffect(() => {
    Object.values(sizeLabelsRef.current).forEach((label) => {
      if (!label) return;
      if (label.material?.diffuseTexture) {
        label.material.diffuseTexture.dispose();
      }
      label.material?.dispose();
      label.dispose();
    });
    sizeLabelsRef.current = {};
    return () => {
      // 🔥 biztos cleanup unmountkor is
      Object.values(sizeLabelsRef.current).forEach((label) => {
        label?.dispose();
      });
      sizeLabelsRef.current = {};

      if (measureTextTextureRef.current) {
        measureTextTextureRef.current.dispose();
        measureTextTextureRef.current = null;
      }
    };
  }, []);
  function createSizeLabel(scene, root, sizeText) {
    const textureWidth = 512;
    const textureHeight = 128;

    const dt = new DynamicTexture(
      `size_label_tex_${root.name}`,
      { width: textureWidth, height: textureHeight },
      scene,
      true
    );

    const ctx = dt.getContext();

    // Transparent háttér
    ctx.clearRect(0, 0, textureWidth, textureHeight);

    ctx.font = "bold 36px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // fekete outline
    ctx.lineWidth = 6;
    ctx.strokeStyle = "black";
    ctx.strokeText(sizeText, textureWidth / 2, textureHeight / 2);

    // fehér szöveg
    ctx.fillStyle = "white";
    ctx.fillText(sizeText, textureWidth / 2, textureHeight / 2);

    dt.hasAlpha = true;
    dt.update();

    const plane = MeshBuilder.CreatePlane(
      `size_label_${root.name}`,
      { width: 0.9, height: 0.25 },
      scene
    );

    plane.billboardMode = Mesh.BILLBOARDMODE_ALL;
    plane.isPickable = false;
    plane.isPickable = false;
    plane.alwaysSelectAsActiveMesh = false;
    plane.doNotSyncBoundingInfo = true;
    const mat = new StandardMaterial(`size_label_mat_${root.name}`, scene);

    mat.diffuseTexture = dt;
    mat.opacityTexture = dt;

    mat.useAlphaFromDiffuseTexture = true;
    mat.transparencyMode = Material.MATERIAL_ALPHABLEND;

    mat.disableLighting = true;
    mat.emissiveColor = new Color3(1, 1, 1);
    mat.backFaceCulling = false;

    // render stabilitás
    mat.disableDepthWrite = true;
    mat.zOffset = -2;

    plane.material = mat;
    plane.renderingGroupId = 2;
    plane.metadata = { isSizeLabel: true };
    return plane;
  }
  function updateSizeLabelPosition(root, label) {
    root.computeWorldMatrix(true);

    const bi = getCombinedBoundingInfo(root);
    if (!bi) return;

    const size = bi.maximum.subtract(bi.minimum);

    let sizeX = size.x;
    let sizeZ = size.z;
    let sizeY = size.y;

    const rotIndex = Math.round(root.rotation.y / (Math.PI / 2)) & 3;
    if (rotIndex === 0) {
      sizeX = size.x / 2;
      sizeZ = size.z;
      sizeY = size.y / 2;
    }
    if (rotIndex === 1) {
      //[sizeY, sizeX] = [sizeX, sizeY];

      sizeX = sizeX / 1;
      sizeZ = sizeZ / -2;
      sizeY = sizeY / 2;
    }
    if (rotIndex === 2) {
      sizeX = sizeX / -2;
      sizeZ = sizeZ / -1;
      sizeY = sizeY / 2;
    }
    if (rotIndex === 3) {
      sizeX = sizeX / -1;
      sizeZ = sizeZ / 2;
      sizeY = sizeY / 2;
    }

    label.position.set(sizeX, sizeY, sizeZ);
  }
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const bounds = getRoomBounds();
    if (!bounds) return;

    const roomW = bounds.w;
    const roomD = bounds.d;
    const roomH = bounds.h;

    Object.entries(groupRootsRef.current).forEach(([oid, root]) => {
      root.computeWorldMatrix(true);
      const bi = getCombinedBoundingInfo(root);
      if (!bi) return;

      const SNAP_EPS = 0.002; // 2mm tolerancia

      // ───────── X tengely ─────────

      // ha jobb falhoz volt tapadva
      if (
        Math.abs(bi.maximum.x - prevRoomSizeRef.current.w * 0.001) < SNAP_EPS
      ) {
        const delta = roomW - prevRoomSizeRef.current.w * 0.001;
        root.position.x += delta;
      }

      // ha bal falhoz volt tapadva → nem kell mozgatni (0 marad)

      // ───────── Z tengely ─────────

      if (
        Math.abs(bi.maximum.z - prevRoomSizeRef.current.d * 0.001) < SNAP_EPS
      ) {
        const delta = roomD - prevRoomSizeRef.current.d * 0.001;
        root.position.z += delta;
      }

      // ───────── Y tengely ─────────

      if (
        Math.abs(bi.maximum.y - prevRoomSizeRef.current.h * 0.001) < SNAP_EPS
      ) {
        const delta = roomH - prevRoomSizeRef.current.h * 0.001;
        root.position.y += delta;
      }

      // ───────── HARD CLAMP ─────────

      root.computeWorldMatrix(true);
      const newBI = getCombinedBoundingInfo(root);
      if (!newBI) return;

      if (newBI.minimum.x < 0) root.position.x -= newBI.minimum.x;
      if (newBI.maximum.x > roomW) root.position.x -= newBI.maximum.x - roomW;

      if (newBI.minimum.z < 0) root.position.z -= newBI.minimum.z;
      if (newBI.maximum.z > roomD) root.position.z -= newBI.maximum.z - roomD;

      if (newBI.minimum.y < 0) root.position.y -= newBI.minimum.y;
      if (newBI.maximum.y > roomH) root.position.y -= newBI.maximum.y - roomH;
    });

    prevRoomSizeRef.current = { ...roomSize };
  }, [roomSize]);
  function resolveCollisionAlongAxis(root, selectedId, axis) {
    const STEP = 0.01; // 1 cm
    const MAX_STEPS = 200; // max 2 méter keresés

    const originalPos = root.position.clone();

    // először pozitív irányba próbálkozunk
    for (let i = 1; i <= MAX_STEPS; i++) {
      root.position[axis] = originalPos[axis] + STEP * i;
      root.computeWorldMatrix(true);

      if (!hasCollision3D(root, selectedId)) {
        return true; // találtunk helyet
      }
    }

    // vissza eredetire
    root.position.copyFrom(originalPos);

    // negatív irányba próbálkozunk
    for (let i = 1; i <= MAX_STEPS; i++) {
      root.position[axis] = originalPos[axis] - STEP * i;
      root.computeWorldMatrix(true);

      if (!hasCollision3D(root, selectedId)) {
        return true;
      }
    }

    // nem találtunk helyet → visszaáll
    root.position.copyFrom(originalPos);
    root.computeWorldMatrix(true);

    return false;
  }
  function getCachedTexture(scene, color) {
    if (!color) return null;
    const key = color.colorId;
    if (textureCacheRef.current.has(key)) {
      return textureCacheRef.current.get(key);
    }
    const img = color.imageData || color.imageDataReduced;
    if (!img) return null;
    const tex = new Texture(`data:image/jpeg;base64,${img}`, scene);
    tex.uRotationCenter = 0.5;
    tex.vRotationCenter = 0.5;
    tex.wAng = Math.PI / 2;
    textureCacheRef.current.set(key, tex);
    return tex;
  }
  function getCachedMaterial(scene, color) {
    const key = color?.colorId ?? "default";
    if (materialCacheRef.current.has(key)) {
      return materialCacheRef.current.get(key);
    }
    const mat = new StandardMaterial(`mat_color_${key}`, scene);
    if (color) {
      const tex = getCachedTexture(scene, color);
      if (tex) {
        mat.diffuseTexture = tex;
        mat.diffuseColor = Color3.White();
      } else {
        mat.diffuseColor = Color3.FromHexString(color.hex || "#ccc");
      }
    } else {
      mat.diffuseColor = new Color3(0.7, 0.7, 0.7);
    }
    // Enyhe felületi csillanás segít a síkok szétválasztásában,
    // de maradjon matt bútorlap-jellegű a megjelenés.
    mat.specularColor = new Color3(0.05, 0.05, 0.05);
    mat.specularPower = 40;
    materialCacheRef.current.set(key, mat);
    return mat;
  }
  function getMeasureLabelMaterial(scene, texture) {
    if (!measureLabelMaterialRef.current) {
      const mat = new StandardMaterial("measureTextMat", scene);
      mat.emissiveColor = Color3.White();
      mat.useAlphaFromDiffuseTexture = true;
      mat.backFaceCulling = false;
      measureLabelMaterialRef.current = mat;
    }
    measureLabelMaterialRef.current.diffuseTexture = texture;
    measureLabelMaterialRef.current.opacityTexture = texture;
    return measureLabelMaterialRef.current;
  }
  const isUnmountingRef = useRef(false);
  const measureTextTextureRef = useRef(null);
  const [showRoomInfo, setShowRoomInfo] = useState(false);
  const roomInfoRef = useRef(null);
  const activeInfoMode = measureMode
    ? "measure"
    : transformMode === "move"
      ? "move"
      : transformMode === "rotate"
        ? "rotate"
        : "idle";
  function SnapInfo() {
    return (
      <div
        style={{
          marginTop: "0.6rem",
          paddingTop: "0.4rem",
          borderTop: "1px dashed #ddd",
          fontSize: "0.65rem",
          lineHeight: 1.4
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
          {t("snapInfo")}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "0.2rem 0.5rem"
          }}
        >
          <span>{t("snapToFurniture")}</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <KeyHint label="Shift" />
            <MouseIcon button="left" />
          </div>

          <span>{t("snapToWall")}</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <KeyHint label="Shift" />
            <MouseIcon button="left" />
          </div>

          <span>{t("snapSelect1")}</span>
          <MouseIcon button="left" />

          <span>{t("snapSelect2")}</span>
          <MouseIcon button="left" />
        </div>
      </div>
    );
  }
  function ShortcutList() {
    return (
      <div
        style={{
          marginTop: "0.6rem",
          paddingTop: "0.4rem",
          borderTop: "1px dashed #ddd",
          fontSize: "0.65rem",
          lineHeight: 1.4
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
          {t("shortcuts")}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "0.25rem 0.5rem"
          }}
        >
          <span>{t("shortcutMeasure")}</span>
          <KeyHint label="M" />
          <span>{t("shortcutMoveRotate")}</span>
          <KeyHint label="F" />
          <span>{t("shortcutSnap")}</span>
          <KeyHint label="T" />
          <span>{t("shortcutSelectSnap")}</span>
          <KeyHint label="Shift + Click" />
          <span>{t("shortcutInfo")}</span>
          <KeyHint label="I" />
          <span>{t("shortcutEscape")}</span>
          <KeyHint label="Esc" />
          <span>{t("shortcutSizeLabels")}</span>
          <KeyHint label="L" />
        </div>
      </div>
    );
  }
  function getMeasureTextTexture(scene, width, height) {
    if (
      !measureTextTextureRef.current ||
      measureTextTextureRef.current.getSize().width < width ||
      measureTextTextureRef.current.getSize().height < height
    ) {
      if (measureTextTextureRef.current) {
        measureTextTextureRef.current.dispose();
      }
      measureTextTextureRef.current = new DynamicTexture(
        "measureText",
        { width, height },
        scene,
        false
      );
    }
    return measureTextTextureRef.current;
  }
  useEffect(() => {
    const onKeyDown = (e) => {
      // ⛔ ne zavarjunk be inputnál / modalnál
      const tag = document.activeElement?.tagName;
      if (isModalOpen) return;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (isDraggingRef.current) return;
      switch (e.key.toLowerCase()) {
        case "m": {
          // mérés
          clearMeasurement(sceneRef.current);
          setFirstPick(null);
          setMeasureMode((p) => !p);
          break;
        }
        case "f": {
          // move ↔ rotate toggle
          clearMeasurement(sceneRef.current);
          setFirstPick(null);
          setMeasureMode(false);
          setTransformMode((prev) => (prev === "move" ? "rotate" : "move"));
          break;
        }
        case "t": {
          // snapping
          setIsSnappingEnabled((p) => !p);
          break;
        }
        case "i": {
          // info panel
          setShowRoomInfo((p) => !p);
          break;
        }
        case "escape": {
          // globális reset
          clearMeasurement(sceneRef.current);
          setFirstPick(null);
          setMeasureMode(false);
          setShowRoomInfo(false);
          break;
        }
        case "l": {
          // size label ki / be
          setShowSizeLabels((p) => !p);
          break;
        }
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isModalOpen]);
  function DragHint({ axis }) {
    return (
      <span style={{ fontSize: "0.65rem", opacity: 0.8 }}>
        {t("drag")} {axis && `(${axis})`}
      </span>
    );
  }
  function MouseIcon({ button }) {
    const isLeft = button === "left";
    const isRight = button === "right";
    return (
      <div
        style={{
          width: 14,
          height: 20,
          border: "1px solid #444",
          borderRadius: "7px",
          position: "relative",
          background: "#f9f9f9"
        }}
      >
        {/* bal gomb */}
        <div
          style={{
            position: "absolute",
            top: 1,
            left: 1,
            width: 5,
            height: 7,
            borderRadius: "3px",
            background: isLeft ? "#333" : "#ccc"
          }}
        />
        {/* jobb gomb */}
        <div
          style={{
            position: "absolute",
            top: 1,
            right: 1,
            width: 5,
            height: 7,
            borderRadius: "3px",
            background: isRight ? "#333" : "#ccc"
          }}
        />
      </div>
    );
  }
  /****************** */
  function MeasureInfo() {
    return (
      <div
        style={{
          marginTop: "0.6rem",
          paddingTop: "0.4rem",
          borderTop: "1px dashed #ddd",
          fontSize: "0.65rem",
          lineHeight: 1.4
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
          {t("measureMode")}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "0.2rem 0.5rem"
          }}
        >
          <span>{t("measureSelectPoint")}</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <KeyHint label="Shift" />
            <MouseIcon button="left" />
          </div>
          <span>{t("measureFinish")}</span>

          <MouseIcon button="left" />
        </div>
      </div>
    );
  }
  function MoveInfo() {
    return (
      <div
        style={{
          marginTop: "0.6rem",
          paddingTop: "0.4rem",
          borderTop: "1px dashed #ddd",
          fontSize: "0.65rem",
          lineHeight: 1.4
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
          {t("moveMode")}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "0.2rem 0.5rem"
          }}
        >
          <span>{t("horizontal")}</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <MouseIcon button="left" />
            <DragHint axis="XZ" />
          </div>
          <span>{t("vertical")}</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <MouseIcon button="right" />
            <DragHint axis="Y" />
          </div>
        </div>
      </div>
    );
  }
  function RotateInfo() {
    return (
      <div
        style={{
          marginTop: "0.6rem",
          paddingTop: "0.4rem",
          borderTop: "1px dashed #ddd",
          fontSize: "0.65rem",
          lineHeight: 1.4
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
          {t("rotateMode")}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "0.2rem 0.5rem"
          }}
        >
          <span>{t("horizontal")}</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <MouseIcon button="left" />
            <DragHint axis="Y" />
          </div>
          <span>{t("tilt")}</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <MouseIcon button="right" />
            <DragHint axis="X" />
          </div>
        </div>
      </div>
    );
  }
  function KeyHint({ label }) {
    return (
      <span
        style={{
          padding: "0 0.3rem",
          border: "1px solid #aaa",
          borderRadius: 4,
          fontSize: "0.6rem",
          background: "#f3f3f3"
        }}
      >
        {label}
      </span>
    );
  }
  function RoomSizeSection({ roomSize, onRoomSizeChange, dimLabels }) {
    const [localRoomSize, setLocalRoomSize] = React.useState(roomSize);
    // ha kívülről változik a roomSize → frissítsük a lokális értéket
    useEffect(() => {
      setLocalRoomSize(roomSize);
    }, [roomSize]);
    const handleChange = (ax, value) => {
      setLocalRoomSize((prev) => ({
        ...prev,
        [ax]: value
      }));
    };
    const handleBlur = (ax) => {
      const v = Number(localRoomSize[ax]) || 0;
      onRoomSizeChange(ax, v);
    };
    return (
      <div>
        <div
          style={{
            fontSize: "0.7rem",
            fontWeight: 600,
            opacity: 0.85
          }}
        >
          {t("roomSizeMm")}
        </div>
        {["w", "d", "h"].map((ax) => (
          <div
            key={ax}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              marginTop: "0.15rem"
            }}
          >
            <span
              style={{
                width: 75,
                fontSize: "0.65rem",
                opacity: 0.85
              }}
            >
              {dimLabels[ax]}:
            </span>
            <Form.Control
              type="number"
              size="sm"
              style={{
                width: 70,
                padding: "0.1rem 0.25rem",
                fontSize: "0.65rem"
              }}
              value={localRoomSize[ax]}
              onChange={(e) => handleChange(ax, e.target.valueAsNumber || 0)}
              onBlur={() => handleBlur(ax)}
            />
          </div>
        ))}
      </div>
    );
  }
  function createMeasureInnerCircle(scene, position, normal, material) {
    const disk = MeshBuilder.CreateCylinder(
      "measure_inner",
      {
        diameter: 0.02, // belső kör mérete
        height: 0.0008, // nagyon lapos
        tessellation: 32
      },
      scene
    );
    disk.position = position.clone();
    disk.material = material;
    disk.isPickable = false;
    // 🔑 ORIENTÁLÁS A FELÜLET NORMÁLJÁRA
    if (normal) {
      const n = normal.normalize();
      const q = Quaternion.FromLookDirectionLH(n, Vector3.Up());
      const correction = Quaternion.RotationAxis(Vector3.Right(), Math.PI / 2);
      disk.rotationQuaternion = q.multiply(correction);
    }
    return disk;
  }
  function createMeasureCenterDot(scene, position, normal) {
    const mat = new StandardMaterial("measureCenterMat", scene);
    mat.diffuseColor = Color3.Black();
    mat.emissiveColor = Color3.Black();
    mat.specularColor = Color3.Black();
    mat.disableLighting = true;
    const dot = MeshBuilder.CreateCylinder(
      "measure_center_dot",
      {
        diameter: 0.008, // kisebb, mint a zöld belső kör
        height: 0.0006, // nagyon lapos
        tessellation: 24
      },
      scene
    );
    dot.position = position.clone();
    dot.material = mat;
    dot.isPickable = false;
    if (normal) {
      const n = normal.normalize();
      const q = Quaternion.FromLookDirectionLH(n, Vector3.Up());
      const correction = Quaternion.RotationAxis(Vector3.Right(), Math.PI / 2);
      dot.rotationQuaternion = q.multiply(correction);
      // 🔒 picit elemeljük, hogy biztos ne Z-fightoljon
      dot.position.addInPlace(n.scale(0.0005));
    }
    return dot;
  }
  function safeDispose(mesh) {
    if (!mesh) return;
    if (mesh.material) {
      if (mesh.material.diffuseTexture) {
        mesh.material.diffuseTexture.dispose();
      }
      mesh.material.dispose();
    }
    mesh.dispose();
  }
  function clearMeasurement(scene) {
    const v = measureVisualsRef.current;
    safeDispose(v.p1);
    safeDispose(v.p2);
    safeDispose(v.line);
    safeDispose(v.label);
    safeDispose(v.p1dot);
    safeDispose(v.p2dot);
    safeDispose(v.p1center);
    safeDispose(v.p2center);
    measureVisualsRef.current = {
      p1: null,
      p1dot: null,
      p1center: null,
      p2: null,
      p2dot: null,
      p2center: null,
      line: null,
      label: null
    };
  }
  function orientRingToNormal(ring, normal) {
    if (!normal) return;
    const n = normal.normalize();
    // Babylon-stabil API
    const q = Quaternion.FromLookDirectionLH(n, Vector3.Up());
    // torus korrekció: Z-alapú orientáció → Y-alapú
    const correction = Quaternion.RotationAxis(Vector3.Right(), Math.PI / 2);
    ring.rotationQuaternion = q.multiply(correction);
  }
  const measureMaterialRef = useRef(null);
  function getMeasureMaterial(scene) {
    if (!measureMaterialRef.current) {
      const mat = new StandardMaterial("measureMat", scene);
      mat.diffuseColor = new Color3(0, 1, 0);
      mat.emissiveColor = new Color3(0, 1, 0);
      mat.specularColor = Color3.Black();
      mat.disableLighting = true;
      measureMaterialRef.current = mat;
    }
    return measureMaterialRef.current;
  }
  function hasCollision3D(root, selectedId) {
    const bi = getCombinedBoundingInfo(root);
    if (!bi) return false;
    const EPS_XZ = 0.001; // 1 mm
    const EPS_Y = 0.01; // ⬅️ 1 cm stack tolerancia
    for (const [oid, other] of Object.entries(groupRootsRef.current)) {
      if (+oid === selectedId) continue;
      const obi = getCombinedBoundingInfo(other);
      if (!obi) continue;
      const overlapX =
        Math.min(bi.maximum.x, obi.maximum.x) -
        Math.max(bi.minimum.x, obi.minimum.x);
      const overlapY =
        Math.min(bi.maximum.y, obi.maximum.y) -
        Math.max(bi.minimum.y, obi.minimum.y);
      const overlapZ =
        Math.min(bi.maximum.z, obi.maximum.z) -
        Math.max(bi.minimum.z, obi.minimum.z);
      const collides =
        overlapX > EPS_XZ && overlapZ > EPS_XZ && overlapY > EPS_Y; // 🔑 EZ A DÖNTŐ
      if (collides) return true;
    }
    return false;
  }
  const isSnappingEnabledRef = useRef(isSnappingEnabled);
  useEffect(() => {
    isSnappingEnabledRef.current = isSnappingEnabled;
  }, [isSnappingEnabled]);
  const transformModeRef = useRef(transformMode);
  useEffect(() => {
    transformModeRef.current = transformMode;
  }, [transformMode]);
  useEffect(() => {
    if (selectedObjectId == null) {
      Object.values(groupRootsRef.current).forEach((root) =>
        root.getChildMeshes().forEach((m) => (m.renderOutline = false))
      );
      return;
    }
  }, [selectedObjectId]);
  function setEdgesForRoot(root, mode) {
    root.getChildMeshes().forEach((m) => {
      if (m.metadata?.isSizeLabel || m.metadata?.isMillingOverlay) return;
      if (m.metadata?.isMillingSolid) {
        m.disableEdgesRendering();
        m.renderOutline = false;
        m.showBoundingBox = mode === "active" || mode === "collision";
        return;
      }
      if (!m.edgesRenderer) m.enableEdgesRendering();
      if (mode === "active") {
        m.edgesColor = new Color4(0, 1, 0, 1); // zöld
        m.edgesWidth = baseEdgeWidthRef.current * 2.2;
      } else if (mode === "collision") {
        m.edgesColor = new Color4(1, 0, 0, 1); // 🔴 piros
        m.edgesWidth = baseEdgeWidthRef.current * 2.5;
      } else {
        m.edgesColor = new Color4(0, 0, 0, 1);
        m.edgesWidth = baseEdgeWidthRef.current / 2.2;
      }
    });
  }
  function snapOnPointerUp(root, selectedId) {
    const SNAP_M = SNAP_THRESHOLD_MM / 1000;
    const bi = getCombinedBoundingInfo(root);
    if (!bi) return;
    const localMin = bi.minimum.subtract(root.position);
    const localMax = bi.maximum.subtract(root.position);
    let bestDx = null;
    let bestDz = null;
    for (const [oid, other] of Object.entries(groupRootsRef.current)) {
      if (+oid === selectedId) continue;
      const obi = getCombinedBoundingInfo(other);
      if (!obi) continue;
      // Z vetületi átfedés → X snap
      const zOverlap =
        root.position.z + localMax.z >= obi.minimum.z &&
        root.position.z + localMin.z <= obi.maximum.z;
      if (zOverlap) {
        const dx1 = obi.maximum.x - (root.position.x + localMin.x);
        const dx2 = obi.minimum.x - (root.position.x + localMax.x);
        if (
          Math.abs(dx1) < SNAP_M &&
          (bestDx === null || Math.abs(dx1) < Math.abs(bestDx))
        ) {
          bestDx = dx1;
        }
        if (
          Math.abs(dx2) < SNAP_M &&
          (bestDx === null || Math.abs(dx2) < Math.abs(bestDx))
        ) {
          bestDx = dx2;
        }
      }
      // X vetületi átfedés → Z snap
      const xOverlap =
        root.position.x + localMax.x >= obi.minimum.x &&
        root.position.x + localMin.x <= obi.maximum.x;
      if (xOverlap) {
        const dz1 = obi.maximum.z - (root.position.z + localMin.z);
        const dz2 = obi.minimum.z - (root.position.z + localMax.z);
        if (
          Math.abs(dz1) < SNAP_M &&
          (bestDz === null || Math.abs(dz1) < Math.abs(bestDz))
        ) {
          bestDz = dz1;
        }
        if (
          Math.abs(dz2) < SNAP_M &&
          (bestDz === null || Math.abs(dz2) < Math.abs(bestDz))
        ) {
          bestDz = dz2;
        }
      }
    }
    if (bestDx !== null) root.position.x += bestDx;
    if (bestDz !== null) root.position.z += bestDz;
  }
  function snapToRoomBounds(root) {
    const SNAP_M = SNAP_THRESHOLD_ROOM_MM / 1000;

    const bi = getCombinedBoundingInfo(root);
    if (!bi) return;

    const bounds = getRoomBounds();
    const roomW = bounds.w;
    const roomD = bounds.d;
    const roomH = bounds.h;

    // --- X tengely (bal / jobb fal) ---
    const distLeft = bi.minimum.x - 0;
    const distRight = roomW - bi.maximum.x;

    if (
      Math.abs(distLeft) < SNAP_M &&
      Math.abs(distLeft) <= Math.abs(distRight)
    ) {
      root.position.x -= distLeft;
    } else if (Math.abs(distRight) < SNAP_M) {
      root.position.x += distRight;
    }

    // --- Z tengely (front / back fal) ---
    const distFront = bi.minimum.z - 0;
    const distBack = roomD - bi.maximum.z;

    if (
      Math.abs(distFront) < SNAP_M &&
      Math.abs(distFront) <= Math.abs(distBack)
    ) {
      root.position.z -= distFront;
    } else if (Math.abs(distBack) < SNAP_M) {
      root.position.z += distBack;
    }

    // --- Y tengely (padló / plafon) ---
    const distFloor = bi.minimum.y - 0;
    const distCeil = roomH - bi.maximum.y;

    if (
      Math.abs(distFloor) < SNAP_M &&
      Math.abs(distFloor) <= Math.abs(distCeil)
    ) {
      root.position.y -= distFloor; // padlóhoz snap
    } else if (Math.abs(distCeil) < SNAP_M) {
      root.position.y += distCeil; // plafonhoz snap
    }
  }

  function clampRootInsideRoom(root) {
    root.computeWorldMatrix(true);
    const bi = getCombinedBoundingInfo(root);
    const bounds = getRoomBounds();
    if (!bi || !bounds) return;

    let dx = 0;
    let dy = 0;
    let dz = 0;

    if (bi.minimum.x < 0) dx = -bi.minimum.x;
    else if (bi.maximum.x > bounds.w) dx = bounds.w - bi.maximum.x;

    if (bi.minimum.y < 0) dy = -bi.minimum.y;
    else if (bi.maximum.y > bounds.h) dy = bounds.h - bi.maximum.y;

    if (bi.minimum.z < 0) dz = -bi.minimum.z;
    else if (bi.maximum.z > bounds.d) dz = bounds.d - bi.maximum.z;

    if (dx || dy || dz) {
      root.position.x += dx;
      root.position.y += dy;
      root.position.z += dz;
      root.computeWorldMatrix(true);
    }
  }

  function isRootInsideRoom(root, epsilon = 0.0001) {
    root.computeWorldMatrix(true);
    const bi = getCombinedBoundingInfo(root);
    const bounds = getRoomBounds();
    if (!bi || !bounds) return false;

    return (
      bi.minimum.x >= -epsilon &&
      bi.minimum.y >= -epsilon &&
      bi.minimum.z >= -epsilon &&
      bi.maximum.x <= bounds.w + epsilon &&
      bi.maximum.y <= bounds.h + epsilon &&
      bi.maximum.z <= bounds.d + epsilon
    );
  }

  function snapObjectToWall(root, wallName) {
    root.computeWorldMatrix(true);
    const SNAP_M = SNAP_THRESHOLD_ROOM_MM / 1000;

    const bi = getCombinedBoundingInfo(root);
    if (!bi) return;

    const { w, d, h } = roomSize;
    const roomW = w * 0.001;
    const roomD = d * 0.001;
    const roomH = h * 0.001;

    const near = (v) => Math.abs(v) < SNAP_M;

    if (wallName.includes("leftWall")) {
      const delta = 0 - bi.minimum.x;
      root.position.x += delta;
    }

    if (wallName.includes("rightWall")) {
      const delta = roomW - bi.maximum.x;
      root.position.x += delta;
    }

    if (wallName.includes("frontWall")) {
      const delta = 0 - bi.minimum.z;
      root.position.z += delta;
    }

    if (wallName.includes("backWall")) {
      const delta = roomD - bi.maximum.z;
      root.position.z += delta;
    }

    if (wallName.includes("ceiling")) {
      const delta = roomH - bi.maximum.y;
      root.position.y += delta;
    }

    // 🔥 SAROK SNAP
    // ha X fal + Z fal közel van → mindkettőre snap
    const distLeft = bi.minimum.x;
    const distRight = roomW - bi.maximum.x;
    const distFront = bi.minimum.z;
    const distBack = roomD - bi.maximum.z;

    if (near(distLeft) && near(distFront)) {
      root.position.x -= distLeft;
      root.position.z -= distFront;
    }

    if (near(distLeft) && near(distBack)) {
      root.position.x -= distLeft;
      root.position.z += distBack;
    }

    if (near(distRight) && near(distFront)) {
      root.position.x += distRight;
      root.position.z -= distFront;
    }

    if (near(distRight) && near(distBack)) {
      root.position.x += distRight;
      root.position.z += distBack;
    }
  }
  function snapObjectNextTo(sourceRoot, targetRoot, selectedId) {
    sourceRoot.computeWorldMatrix(true);
    targetRoot.computeWorldMatrix(true);

    const originalPos = sourceRoot.position.clone();

    const sBI = getCombinedBoundingInfo(sourceRoot);
    const tBI = getCombinedBoundingInfo(targetRoot);
    if (!sBI || !tBI) return;

    const axes = ["x", "y", "z"];

    // ── 1. Elsődleges tengely: melyik irányból érintkeznek? ──
    const candidates = axes.map((axis) => {
      const sMin = sBI.minimum[axis];
      const sMax = sBI.maximum[axis];
      const tMin = tBI.minimum[axis];
      const tMax = tBI.maximum[axis];
      const delta1 = tMin - sMax; // source max → target min
      const delta2 = tMax - sMin; // source min → target max
      const best = Math.abs(delta1) < Math.abs(delta2) ? delta1 : delta2;
      return { axis, delta: best };
    });

    const orderedCandidates = [...candidates].sort(
      (a, b) => Math.abs(a.delta) - Math.abs(b.delta)
    );

    const EDGE_SNAP_THRESHOLD = 0.3;

    const tryCandidate = (primary) => {
      sourceRoot.position.copyFrom(originalPos);
      sourceRoot.position[primary.axis] += primary.delta;
      sourceRoot.computeWorldMatrix(true);

      const newBI = getCombinedBoundingInfo(sourceRoot);
      if (!newBI) return false;

      axes
        .filter((axis) => axis !== primary.axis)
        .forEach((axis) => {
          const sMin = newBI.minimum[axis];
          const sMax = newBI.maximum[axis];
          const tMin = tBI.minimum[axis];
          const tMax = tBI.maximum[axis];
          const edgePairs = [
            { delta: tMin - sMin },
            { delta: tMax - sMax },
            { delta: tMin - sMax },
            { delta: tMax - sMin }
          ];
          const best = edgePairs.reduce((a, b) =>
            Math.abs(a.delta) < Math.abs(b.delta) ? a : b
          );

          if (Math.abs(best.delta) < EDGE_SNAP_THRESHOLD) {
            sourceRoot.position[axis] += best.delta;
          } else {
            const sCenter = (sMin + sMax) / 2;
            const tCenter = (tMin + tMax) / 2;
            sourceRoot.position[axis] += tCenter - sCenter;
          }
        });

      sourceRoot.computeWorldMatrix(true);
      if (!isRootInsideRoom(sourceRoot)) return false;

      if (hasCollision3D(sourceRoot, selectedId)) {
        const success = resolveCollisionAlongAxis(
          sourceRoot,
          selectedId,
          primary.axis
        );
        if (!success) return false;

        sourceRoot.computeWorldMatrix(true);
        if (
          !isRootInsideRoom(sourceRoot) ||
          hasCollision3D(sourceRoot, selectedId)
        ) {
          return false;
        }
      }

      return true;
    };

    const success = orderedCandidates.some((candidate) =>
      tryCandidate(candidate)
    );

    if (!success) {
      sourceRoot.position.copyFrom(originalPos);
      sourceRoot.computeWorldMatrix(true);
    }
  }

  function serializeRootTransform(root) {
    return {
      position: [
        root.position.x.toFixed(3),
        root.position.y.toFixed(3),
        root.position.z.toFixed(3)
      ],
      rotation: [
        Math.round(Tools.ToDegrees(root.rotation.x)),
        Math.round(Tools.ToDegrees(root.rotation.y)),
        Math.round(Tools.ToDegrees(root.rotation.z))
      ]
    };
  }
  useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
      // 🔥 UNMOUNT → local commit
      Object.entries(groupRootsRef.current).forEach(([oid, root]) => {
        const obj = objects.find((o) => o.objectId === +oid);
        if (!obj || !root) return;
        const t = serializeRootTransform(root);
        const updatedObject = {
          ...obj,
          position: `[${t.position.join(",")}]`,
          rotation: `[${t.rotation.join(",")}]`
        };
        const idx = objects.findIndex((o) => o.objectId === +oid);
        if (idx !== -1 && typeof onObjectUpdate === "function") {
          onObjectUpdate(updatedObject, idx);
        }
      });
    };
  }, []);
  useEffect(() => {
    controlModeRef.current = controlMode;
  }, [controlMode]);
  useEffect(() => {
    const camera = cameraRef.current;
    const canvas = canvasRef.current;
    if (!camera || !canvas) return;
    if (controlMode === "object") {
      camera.detachControl(canvas);
    } else {
      camera.attachControl(canvas, true);
    }
  }, [controlMode]);
  useEffect(() => {
    const sysIds = new Set(
      objects.filter((o) => o.system === true).map((o) => o.objectId)
    );
    systemObjectIds.current = sysIds;
  }, [objects]);
  // ha változik a state, tartsuk frissen a ref-eket:
  useEffect(() => {
    measureModeRef.current = measureMode;
  }, [measureMode]);
  useEffect(() => {
    firstPickRef.current = firstPick;
  }, [firstPick]);
  const itemMeshesRef = useRef({});
  // store original position/rotation when opening modal
  const originalRef = useRef({
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 }
  });
  // draggable modal
  const [modalPos, setModalPos] = useState({
    x: window.innerWidth / 15,
    y: window.innerHeight / 5
  });
  useEffect(() => {
    const dialog = document.querySelector(".modal-dialog");
    if (dialog) {
      dialog.style.position = "absolute";
      dialog.style.top = `${modalPos.y}px`;
      dialog.style.left = `${modalPos.x}px`;
      dialog.style.margin = "0";
      dialog.style.transform = "none"; // <— Ezt kell hozzáadni
      dialog.style.width = "auto"; // vagy tetszőleges fix/százalék szélesség
    }
  }, [modalPos, isModalOpen]);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, origX: 0, origY: 0 });

  const baseRadiusRef = useRef(null);
  const baseEdgeWidthRef = useRef(0.6); // induló élvastagság px-ben
  function updateEdgeWidths(scene, camera) {
    if (!baseRadiusRef.current) baseRadiusRef.current = camera.radius;
    const r0 = baseRadiusRef.current;
    const scale = camera.radius / r0; // közelebb -> <1
    const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
    scene.meshes.forEach((m) => {
      if (!m.edgesRenderer) return;
      const baseW = m.metadata?.edgeBaseW ?? baseEdgeWidthRef.current;
      m.edgesWidth = clamp(baseW * scale, 0.05, 2); // ízlés szerinti határok
    });
  }
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setModalPos({
        x: dragRef.current.origX + dx,
        y: dragRef.current.origY + dy
      });
    };
    const onMouseUp = () => setDragging(false);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging]);
  useEffect(() => {
    if (sceneOwnsTransformRef.current) return;
    objects.forEach((o) => {
      const root = groupRootsRef.current[o.objectId];
      if (!root) return;
      const [x, y, z] = parsePositionString(o.position || "[0,0,0]", 1)[0];
      const [rx, ry, rz] = parseRotationString(o.rotation || "[0,0,0]", 1)[0];
      root.position.set(x, y, z);
      root.rotation.set(
        Tools.ToRadians(rx),
        Tools.ToRadians(ry),
        Tools.ToRadians(rz)
      );
    });
  }, [objects]);
  useEffect(() => {
    createdItems.forEach((it) => {
      const meshes = itemMeshesRef.current[it.itemId];
      if (!meshes) return;
      const pos = parseBracketList(it.position, it.qty);
      const rot = parseBracketList(it.rotation, it.qty);
      meshes.forEach((mesh, i) => {
        mesh.position.set(
          pos[i][0] * SCALE,
          pos[i][1] * SCALE,
          pos[i][2] * SCALE
        );
        mesh.rotation.set(rot[i][0], rot[i][1], rot[i][2]);
      });
    });
  }, [createdItems]);

  useEffect(() => {
    const objectItems = new Map();

    createdItems.forEach((it) => {
      const meshes = itemMeshesRef.current[it.itemId];
      const rawObjectId = it.object?.objectId;
      const itemId = it.itemId ?? it.tmpId;
      const oid = rawObjectId == null ? null : String(rawObjectId);

      if (oid != null) {
        const items = objectItems.get(oid) || [];
        items.push(it);
        objectItems.set(oid, items);
      }

      if (meshes) {
        const isSystem = systemObjectIds.current.has(rawObjectId);
        const isHidden = isSystem || hiddenItems.has(itemId);
        meshes.forEach((mesh) => {
          mesh.isVisible = !isHidden;
          mesh.isPickable = !isHidden;
        });
      }
    });

    const hiddenObjectIds = new Set();
    objectItems.forEach((items, oid) => {
      if (
        items.length > 0 &&
        items.every((item) => hiddenItems.has(item.itemId ?? item.tmpId))
      ) {
        hiddenObjectIds.add(oid);
      }
    });
    hiddenObjectIdsRef.current = hiddenObjectIds;

    Object.entries(groupRootsRef.current).forEach(([oid, root]) => {
      const isHidden = hiddenObjectIds.has(String(oid));
      root.setEnabled(!isHidden);

      const label = sizeLabelsRef.current[oid];
      label?.setEnabled(!isHidden && showSizeLabelsRef.current);

      if (isHidden && String(selectedObjectIdRef.current) === String(oid)) {
        selectedObjectIdRef.current = null;
        setSelectedObjectId(null);
        setIsModalOpen(false);
        isDraggingRef.current = false;
        dragModeRef.current = null;
        shiftSnapFirstRef.current = null;
        root.getChildMeshes().forEach((mesh) => {
          mesh.renderOutline = false;
        });
        if (controlModeRef.current === "camera") {
          cameraRef.current?.attachControl(canvasRef.current, true);
        } else {
          cameraRef.current?.detachControl(canvasRef.current);
        }
      }
    });
  }, [createdItems, hiddenItems]);

  function updateMaterial(mat, color) {
    if (!mat) return;
    const oldTexture = mat.diffuseTexture;
    textureCacheRef.current.delete(color.colorId);
    mat.diffuseTexture = getCachedTexture(mat.getScene(), color);
    if (oldTexture && oldTexture !== mat.diffuseTexture) oldTexture.dispose();
    mat.diffuseColor = mat.diffuseTexture ? Color3.White() : Color3.FromHexString(color.hex || "#cccccc");
    mat.markAsDirty();
  }
  useEffect(() => {
    const updatedMaterials = new Set();
    createdItems.forEach((it) => {
      const meshes = itemMeshesRef.current[it.itemId];
      if (!meshes) return;
      const col = usedColors.find((c) => c.colorId === it.color?.colorId);
      meshes.forEach((mesh) => {
        if (!mesh.material || updatedMaterials.has(mesh.material)) return;
        updatedMaterials.add(mesh.material);
        if (!col) {
          // nincs szín vagy PFL → alapértelmezett szürke
          if (mesh.material.diffuseTexture) {
            mesh.material.diffuseTexture.dispose();
            mesh.material.diffuseTexture = null;
          }
          mesh.material.diffuseColor = new Color3(0.7, 0.7, 0.7);
          mesh.material.markAsDirty();
        } else {
          updateMaterial(mesh.material, col);
        }
      });
    });
    const activeColorIds = new Set(usedColors.map(color => color.colorId));
    textureCacheRef.current.forEach((texture, id) => {
      if (!activeColorIds.has(id)) {
        const material = materialCacheRef.current.get(id);
        if (material) material.diffuseTexture = null;
        texture.dispose();
        textureCacheRef.current.delete(id);
      }
    });
  }, [createdItems, usedColors, millingDisplay.show3D]);
  useEffect(() => {
    createdItems.forEach((it) => {
      const meshes = itemMeshesRef.current[it.itemId];
      if (!meshes) return;
      const oid = it.object?.objectId;
      const isSystem = systemObjectIds.current.has(oid);
      meshes.forEach((m) => {
        const isHidden = isSystem || hiddenItems.has(it.itemId ?? it.tmpId);
        m.isVisible = !isHidden;
        m.isPickable = !isHidden;
      });
    });
  }, [createdItems, hiddenItems]);

  function getCombinedBoundingInfo(root) {
    const meshes = root
      .getChildMeshes()
      .filter((m) => !m.metadata?.isSizeLabel);
    if (meshes.length === 0) return null;
    let min = new Vector3(+Infinity, +Infinity, +Infinity);
    let max = new Vector3(-Infinity, -Infinity, -Infinity);
    meshes.forEach((mesh) => {
      const bb = mesh.getBoundingInfo().boundingBox;
      const world = mesh.computeWorldMatrix(true);
      const corners = [
        bb.minimum,
        new Vector3(bb.maximum.x, bb.minimum.y, bb.minimum.z),
        new Vector3(bb.minimum.x, bb.maximum.y, bb.minimum.z),
        new Vector3(bb.minimum.x, bb.minimum.y, bb.maximum.z),
        new Vector3(bb.maximum.x, bb.maximum.y, bb.minimum.z),
        new Vector3(bb.minimum.x, bb.maximum.y, bb.maximum.z),
        new Vector3(bb.maximum.x, bb.minimum.y, bb.maximum.z),
        bb.maximum
      ];
      corners.forEach((c) => {
        const p = Vector3.TransformCoordinates(c, world);
        min = Vector3.Minimize(min, p);
        max = Vector3.Maximize(max, p);
      });
    });
    return { minimum: min, maximum: max };
  }

  // soften backdrop blur
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .no-blur-backdrop {
        backdrop-filter: none !important;
        background-color: rgba(0,0,0,0.7) !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  // Babylon refs
  const canvasRef = useRef();
  const sceneRef = useRef();
  const cameraRef = useRef();
  const groupRootsRef = useRef({});
  const wallsRef = useRef({});
  const shadowGeneratorRef = useRef(null);
  const ceilingLightRef = useRef(null);
  const roomMaterialsRef = useRef([]);

  // 1) engine + scene + camera
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error("Canvas not found");
      return;
    }
    const engine = new Engine(canvas, true, {
      preserveDrawingBuffer: true,
      stencil: true,
      adaptToDeviceRatio: true
    });
    const scene = new Scene(engine);
    scene.clearColor = new Color4(0.9, 0.9, 0.9, 1);
    sceneRef.current = scene;
    const camera = new ArcRotateCamera(
      "camera",
      Math.PI / 2,
      Math.PI / 4,
      10,
      new Vector3(2.5, 1, 2.5),
      scene
    );
    camera.attachControl(canvas, true);
    camera.minZ = 0.01;
    camera.maxZ = 100;
    camera.lowerRadiusLimit = 0;
    camera.upperRadiusLimit = 15;
    camera.wheelPrecision = 65;
    // --- kamera finomítás ---
    camera.angularSensibilityX = 1200; // default ~1000
    camera.angularSensibilityY = 1200;
    camera.panningSensibility = 2000; // nagyobb = lassabb
    camera.inertia = 0.8; // mozgás "utánhúzás"
    cameraRef.current = camera;
    camera.onViewMatrixChangedObservable.add(() => {
      updateEdgeWidths(scene, camera);
    });
    // Környezeti visszaverődés, hűvös oldalirányú derítés és meleg mennyezeti főfény.
    // Csak a főfény készít árnyéktérképet.
    const hemi = new HemisphericLight("hemilight", new Vector3(0, 1, 0), scene);
    hemi.intensity = 0.65;
    hemi.diffuse = new Color3(1, 1, 1);
    hemi.groundColor = new Color3(0.55, 0.53, 0.5);
    hemi.specular = Color3.Black();
    const daylight = new DirectionalLight(
      "windowFill",
      new Vector3(-0.6, -0.8, -1),
      scene
    );
    daylight.intensity = 0.45;
    daylight.diffuse = new Color3(0.94, 0.97, 1);
    daylight.specular = new Color3(0.25, 0.25, 0.25);

    const initialRoomW = Math.max(0.1, (roomSize?.w || 5000) * 0.001);
    const initialRoomH = Math.max(0.1, (roomSize?.h || 2600) * 0.001);
    const initialRoomD = Math.max(0.1, (roomSize?.d || 5000) * 0.001);

    const ceilingLight = new SpotLight(
      "ceilingLight",
      new Vector3(
        initialRoomW / 2,
        Math.max(0.02, initialRoomH - 0.03),
        initialRoomD / 2
      ),
      new Vector3(0, -1, 0),
      Math.PI * 2,
      0.8,
      scene
    );
    ceilingLight.intensity = 0.7;
    ceilingLight.range = Math.max(initialRoomW, initialRoomH, initialRoomD) * 5;
    ceilingLight.diffuse = new Color3(1, 0.98, 0.95);
    ceilingLight.specular = new Color3(0.65, 0.63, 0.6);
    ceilingLightRef.current = ceilingLight;

    scene.shadowsEnabled = true;
    const shadowGenerator = new ShadowGenerator(2048, ceilingLight);

    // A korábbi blur-exponential shadow map a közel koplanáris bútorfelületeken
    // kamera-mozgáskor vibráló/sávos shadow acne-t tudott okozni. A PCF stabilabb.
    shadowGenerator.useBlurExponentialShadowMap = false;
    shadowGenerator.usePercentageCloserFiltering = true;
    shadowGenerator.filteringQuality = ShadowGenerator.QUALITY_HIGH;
    shadowGenerator.bias = 0.0018;
    shadowGenerator.normalBias = 0.002;
    shadowGenerator.forceBackFacesOnly = true;
    shadowGenerator.setDarkness(0.32);
    shadowGeneratorRef.current = shadowGenerator;
    const pointerObserver1 = scene.onPointerObservable.add((pi) => {
      if (pi.type !== PointerEventTypes.POINTERDOWN) return;
      if (measureModeRef.current) {
        if (!pi.event.shiftKey) return;
        const pick = scene.pick(
          scene.pointerX,
          scene.pointerY,
          (m) =>
            m?.name?.startsWith("box_") ||
            m?.name?.startsWith("room_") ||
            m?.name?.endsWith("_pick"),
          false,
          camera
        );
        if (!pick?.hit || !pick.pickedPoint) return;
        const worldPos = pick.pickedPoint;
        const mmPos = {
          x: worldPos.x * 1000,
          y: worldPos.y * 1000,
          z: worldPos.z * 1000
        };
        if (!firstPickRef.current) {
          clearMeasurement(scene);
          const measureMat = getMeasureMaterial(scene);
          const p1 = MeshBuilder.CreateTorus(
            "measure_p1",
            {
              diameter: 0.045, // kisebb külső kör
              thickness: 0.003,
              tessellation: 48
            },
            scene
          );
          p1.position = pick.pickedPoint.clone();
          p1.material = measureMat;
          p1.isPickable = false;
          const normal0 = pick.getNormal(true)?.clone();
          const dot1 = createMeasureInnerCircle(
            scene,
            p1.position,
            normal0,
            measureMat
          );
          const center1 = createMeasureCenterDot(scene, p1.position, normal0);
          measureVisualsRef.current.p1center = center1;
          measureVisualsRef.current.p1 = p1;
          measureVisualsRef.current.p1dot = dot1;
          setFirstPick({
            pos: mmPos,
            world: pick.pickedPoint.clone(),
            normal: normal0
          });
          orientRingToNormal(p1, normal0);
          return;
        }
        const { pos: p0, world: w0 } = firstPickRef.current;
        const w1 = pick.pickedPoint.clone();
        const measureMat = getMeasureMaterial(scene);
        const normal1 = pick.getNormal(true)?.clone();
        const p2 = MeshBuilder.CreateTorus(
          "measure_p2",
          {
            diameter: 0.045, // kisebb külső kör
            thickness: 0.003,
            tessellation: 48
          },
          scene
        );
        p2.position = w1.clone();
        p2.material = measureMat;
        p2.isPickable = false;
        const dot2 = createMeasureInnerCircle(
          scene,
          p2.position,
          normal1,
          measureMat
        );
        const center2 = createMeasureCenterDot(scene, p2.position, normal1);
        measureVisualsRef.current.p2center = center2;
        measureVisualsRef.current.p2 = p2;
        measureVisualsRef.current.p2dot = dot2;
        orientRingToNormal(p2, normal1);
        // vonal
        const line = MeshBuilder.CreateLines(
          "measure_line",
          { points: [w0, w1] },
          scene
        );
        line.color = new Color3(0, 1, 0);
        line.isPickable = false;
        // távolság számítás
        const dx = Math.abs(mmPos.x - p0.x).toFixed(1);
        const dy = Math.abs(mmPos.y - p0.y).toFixed(1);
        const dz = Math.abs(mmPos.z - p0.z).toFixed(1);
        const dist = Vector3.Distance(w0, w1) * 1000;
        // opcionális szöveg
        const mid = w0.add(w1).scale(0.5);
        const lines = [
          `${t("width")}: ${dx} mm`,
          `${t("height")}: ${dy} mm`,
          `${t("depth")}: ${dz} mm`,
          `${t("measureTotal")}: ${dist.toFixed(1)} mm`
        ];
        const fontSize = 36;
        const lineHeight = fontSize * 1.4;
        const padding = 20;
        // ideiglenes canvas a méréshez
        const measureCanvas = document.createElement("canvas");
        const ctx = measureCanvas.getContext("2d");
        ctx.font = `bold ${fontSize}px Arial`;
        const maxTextWidth = Math.max(
          ...lines.map((l) => ctx.measureText(l).width)
        );
        const texWidth = Math.ceil(maxTextWidth + padding * 2);
        const texHeight = Math.ceil(lines.length * lineHeight + padding * 2);
        const dt = getMeasureTextTexture(scene, 1024, 512);
        const ctx2d = dt.getContext();
        ctx2d.clearRect(0, 0, texWidth, texHeight);
        ctx2d.font = `bold ${fontSize}px Arial`;
        ctx2d.textBaseline = "top";
        ctx2d.lineJoin = "round";
        ctx2d.miterLimit = 2;
        lines.forEach((line, i) => {
          const y = padding + i * lineHeight;
          // fekete körvonal
          ctx2d.lineWidth = 6;
          ctx2d.strokeStyle = "black";
          ctx2d.strokeText(line, padding, y);
          // fehér kitöltés
          ctx2d.fillStyle = "white";
          ctx2d.fillText(line, padding, y);
        });
        dt.update();
        const planeScale = 0.0006; // UI skála (ízlés szerint)
        const plane = MeshBuilder.CreatePlane(
          "measure_label",
          {
            width: texWidth * planeScale,
            height: texHeight * planeScale
          },
          scene
        );
        plane.position = mid;
        plane.billboardMode = Mesh.BILLBOARDMODE_ALL;
        plane.material = getMeasureLabelMaterial(scene, dt);
        measureVisualsRef.current.line = line;
        measureVisualsRef.current.label = plane;
        setFirstPick(null);
        setMeasureMode(false);
        return;
      }
      if (pi.event.button === 2) return;
      const pick = pi.pickInfo;
      if (!pick.hit) return;
      const evt = pi.event;
      if (evt.button === 0 && evt.getModifierState?.("CapsLock")) {
        camera.detachControl(canvasRef.current);
      }
      if (evt.button !== 0 && evt.button !== 2) return;
      const name = pick.pickedMesh?.name;
      if (evt.button === 0) {
        const meshName = pick.pickedMesh?.name;

        // =========================================================
        // 🔥 SHIFT SNAP LOGIKA
        // =========================================================
        if (evt.shiftKey && !measureModeRef.current) {
          // --- első kattintás ---
          if (!shiftSnapFirstRef.current) {
            if (meshName?.startsWith("box_")) {
              const oid = parseInt(meshName.split("_")[1], 10);
              if (hiddenObjectIdsRef.current.has(String(oid))) return;
              shiftSnapFirstRef.current = { type: "object", id: oid };
            }

            return;
          }

          // --- második kattintás ---
          const first = shiftSnapFirstRef.current;
          const root = groupRootsRef.current[first.id];

          if (!root) {
            shiftSnapFirstRef.current = null;
            return;
          }

          // OBJECT → OBJECT
          if (meshName?.startsWith("box_")) {
            const secondId = parseInt(meshName.split("_")[1], 10);
            if (hiddenObjectIdsRef.current.has(String(secondId))) {
              shiftSnapFirstRef.current = null;
              return;
            }

            if (first.id !== secondId) {
              const secondRoot = groupRootsRef.current[secondId];

              if (secondRoot) {
                const originalPos = root.position.clone();
                snapObjectNextTo(root, secondRoot, first.id);
                clampRootInsideRoom(root);
                // vagy snapObjectToWall(root, meshName);

                root.computeWorldMatrix(true);

                if (hasCollision3D(root, first.id)) {
                  root.position.copyFrom(originalPos);
                  root.computeWorldMatrix(true);
                  setEdgesForRoot(root, "collision");
                }
              }
            }
          }

          // OBJECT → WALL
          else if (
            meshName?.startsWith("room_") &&
            meshName?.endsWith("_pick")
          ) {
            const originalPos = root.position.clone();
            snapObjectToWall(root, meshName);

            root.computeWorldMatrix(true);

            if (hasCollision3D(root, first.id)) {
              root.position.copyFrom(originalPos);
              root.computeWorldMatrix(true);
              setEdgesForRoot(root, "collision");
            }
          }

          // 🔥 COMMIT TRANSFORM
          const obj = objectsRef.current.find((o) => o.objectId === first.id);

          if (obj) {
            const t = serializeRootTransform(root);

            const updatedObject = {
              ...obj,
              position: `[${t.position.join(",")}]`,
              rotation: `[${t.rotation.join(",")}]`
            };

            const idx = objectsRef.current.findIndex(
              (o) => o.objectId === first.id
            );

            onObjectUpdate?.(updatedObject, idx);
          }

          shiftSnapFirstRef.current = null;
          return;
        }

        // =========================================================
        // 🟢 NORMÁL BOX KIVÁLASZTÁS (CapsLock + click)
        // =========================================================
        const capsOn = evt.getModifierState?.("CapsLock");

        if (!capsOn) return;
        if (!meshName?.startsWith("box_")) return;

        const oid = parseInt(meshName.split("_")[1], 10);
        if (hiddenObjectIdsRef.current.has(String(oid))) return;
        const root = groupRootsRef.current[oid];
        if (!root) return;

        selectedObjectIdRef.current = oid;
        setSelectedObjectId(oid);
        camera.detachControl(canvasRef.current);
        originalRef.current = {
          position: {
            x: root.position.x * 1000,
            y: root.position.y * 1000,
            z: root.position.z * 1000
          },
          rotation: {
            x: Math.round(Tools.ToDegrees(root.rotation.x)),
            y: Math.round(Tools.ToDegrees(root.rotation.y)),
            z: Math.round(Tools.ToDegrees(root.rotation.z))
          }
        };

        setObjPosition(originalRef.current.position);
        setObjRotation(originalRef.current.rotation);
        setIsModalOpen(true);
      }
      // ref-eket használva mindig a legfrissebb állapotot látjuk:
      if (measureModeRef.current && pi.event.shiftKey) {
        const worldPos = pick.pickedPoint;
        if (!worldPos) return;
        const mmPos = {
          x: worldPos.x * 1000,
          y: worldPos.y * 1000,
          z: worldPos.z * 1000
        };
        if (!firstPickRef.current) {
          setFirstPick({ mesh: pick.pickedMesh.name, pos: mmPos });
          return;
        }
        // második pont → távolság
        const { pos: p0 } = firstPickRef.current;
        const dx = mmPos.x - p0.x;
        const dy = mmPos.y - p0.y; // függőleges különbség
        const dz = mmPos.z - p0.z;
        // abszolút értékek
        const absX = Math.abs(dx).toFixed(1);
        const absY = Math.abs(dy).toFixed(1); // függőleges
        const absZ = Math.abs(dz).toFixed(1);
        alert(
          `${t("measureX")}: ${absX} mm\n` +
            `${t("measureY")}: ${absY} mm\n` +
            `${t("measureZ")}: ${absZ} mm\n`
        );
        setFirstPick(null);
        setMeasureMode(false);
        return;
      }
      // CAPS LOCK + BAL KATT
      if (evt.button !== 0) return;
      const capsOn = evt.getModifierState?.("CapsLock");
      if (!capsOn) return;
      if (!name.startsWith("box_")) return;
      const oid = parseInt(name.split("_")[1], 10);
      if (hiddenObjectIdsRef.current.has(String(oid))) return;
      const root = groupRootsRef.current[oid];
      if (!root) return;
      selectedObjectIdRef.current = oid;
      setSelectedObjectId(oid);
      originalRef.current = {
        position: {
          x: root.position.x * 1000,
          y: root.position.y * 1000,
          z: root.position.z * 1000
        },
        rotation: {
          x: Math.round(Tools.ToDegrees(root.rotation.x)),
          y: Math.round(Tools.ToDegrees(root.rotation.y)),
          z: Math.round(Tools.ToDegrees(root.rotation.z))
        }
      };
      setObjPosition(originalRef.current.position);
      setObjRotation(originalRef.current.rotation);
      //setSelectedObjectId(oid); // 🔴 EZ HIÁNYZOTT
      setIsModalOpen(true);
    });
    const pointerObserver2 = scene.onPointerObservable.add((pi) => {
      if (pi.type === PointerEventTypes.POINTERDOWN) {
        const evt = pi.event;
        if (isDraggingRef.current) return;
        // globális kizárások
        if (isModalOpen) return;
        if (measureModeRef.current) return;
        if (controlModeRef.current !== "object") return;
        // csak bal (0) és jobb (2) gomb érdekel
        if (evt.button !== 0 && evt.button !== 2) return;
        // kizárólag box_* mesh-eket engedünk húzni
        const pick = scene.pick(
          scene.pointerX,
          scene.pointerY,
          (m) => {
            if (!m?.name?.startsWith("box_")) return false;
            const oid = m.name.split("_")[1];
            return !hiddenObjectIdsRef.current.has(String(oid));
          },
          false,
          camera
        );
        if (!pick?.hit || !pick.pickedMesh) return;
        // objektum azonosítása
        const name = pick.pickedMesh.name;
        const oid = parseInt(name.split("_")[1], 10);
        if (hiddenObjectIdsRef.current.has(String(oid))) return;
        const root = groupRootsRef.current[oid];
        if (!root) return;
        selectedObjectIdRef.current = oid;
        if (selectedObjectId !== oid) {
          setSelectedObjectId(oid);
        }
        // ─────────────────────────────────────
        // DRAG MODE DÖNTÉS – TRANSFORM MODE ALAPÚ
        // ─────────────────────────────────────
        const mode = transformModeRef.current;
        const btn = evt.button;
        if (mode === "move") {
          if (btn === 0) {
            dragModeRef.current = "move"; // XZ mozgatás
          } else if (btn === 2) {
            dragModeRef.current = "vertical"; // Y mozgatás
          } else {
            return;
          }
        }
        if (mode === "rotate") {
          if (btn === 0) {
            dragModeRef.current = "rotate-y"; // vízszintes forgatás
          } else if (btn === 2) {
            dragModeRef.current = "rotate-x"; // billentés előre-hátra
          } else {
            return;
          }
        }
        // ─────────────────────────────────────
        // DRAG INICIALIZÁLÁS
        // ─────────────────────────────────────
        // 🔥 MOZGATÁS / FORGATÁS → MÉRÉS TÖRLÉSE
        clearMeasurement(scene);
        firstPickRef.current = null;
        measureModeRef.current = false;
        const planeY = root.position.y;
        const ray = scene.createPickingRay(
          scene.pointerX,
          scene.pointerY,
          Matrix.Identity(),
          camera
        );
        const t = (planeY - ray.origin.y) / ray.direction.y;
        if (!isFinite(t)) return;
        const hitPoint = ray.origin.add(ray.direction.scale(t));
        dragPlanePointRef.current = hitPoint.clone();
        dragStartRootPosRef.current = root.position.clone();
        isDraggingRef.current = true;
        sceneOwnsTransformRef.current = true;
        pointerMovedRef.current = false;
        setEdgesForRoot(root, "active");
        pointerDownXRef.current = {
          x: evt.clientX,
          y: evt.clientY
        };
        lastPointerPosRef.current = null;
        // kamera kikapcsolása drag alatt
        camera.detachControl(canvasRef.current);
      }
      if (pi.type === PointerEventTypes.POINTERMOVE) {
        if (!isDraggingRef.current) return;
        const MOVE_THRESHOLD = 3; // pixel
        const dx = pi.event.clientX - pointerDownXRef.current.x;
        const dy = pi.event.clientY - pointerDownXRef.current.y;
        if (Math.hypot(dx, dy) > MOVE_THRESHOLD) {
          pointerMovedRef.current = true;
        }
        const oid = selectedObjectIdRef.current;
        const root = groupRootsRef.current[oid];
        if (!root) return;
        if (dragModeRef.current === "move") {
          const planeY = dragStartRootPosRef.current.y;
          const ray = scene.createPickingRay(
            scene.pointerX,
            scene.pointerY,
            Matrix.Identity(),
            camera
          );

          const t = (planeY - ray.origin.y) / ray.direction.y;
          if (!isFinite(t)) return;

          const hitPoint = ray.origin.add(ray.direction.scale(t));
          const DRAG_SLOWDOWN = 0.6;
          const delta = hitPoint
            .subtract(dragPlanePointRef.current)
            .scale(DRAG_SLOWDOWN);

          // új pozíció ideiglenesen
          root.position.x = dragStartRootPosRef.current.x + delta.x;
          root.position.z = dragStartRootPosRef.current.z + delta.z;

          // 🔥 bounding box alapú clamp
          const bi = getCombinedBoundingInfo(root);
          if (!bi) return;

          const bounds = getRoomBounds();
          const roomW = bounds.w;
          const roomD = bounds.d;
          // X tengely
          if (bi.minimum.x < 0) {
            root.position.x -= bi.minimum.x;
          }
          if (bi.maximum.x > roomW) {
            root.position.x -= bi.maximum.x - roomW;
          }

          // Z tengely
          if (bi.minimum.z < 0) {
            root.position.z -= bi.minimum.z;
          }
          if (bi.maximum.z > roomD) {
            root.position.z -= bi.maximum.z - roomD;
          }

          // opcionális: padló clamp
          if (bi.minimum.y < 0) {
            root.position.y -= bi.minimum.y;
          }
        }
        if (dragModeRef.current === "rotate-y") {
          if (!lastPointerPosRef.current) {
            lastPointerPosRef.current = { x: pi.event.clientX };
            return;
          }
          const dx = pi.event.clientX - lastPointerPosRef.current.x;
          const ROT_SPEED = 0.002;
          const camForward = camera.getForwardRay().direction.normalize();
          const dirSign = camForward.z < 0 ? 1 : -1;
          root.rotation.y += dx * ROT_SPEED * dirSign;
          lastPointerPosRef.current.x = pi.event.clientX;
          const label = sizeLabelsRef.current[oid];
          if (label) updateSizeLabelPosition(root, label);
        }
        if (dragModeRef.current === "rotate-x") {
          if (!lastPointerPosRef.current) {
            lastPointerPosRef.current = { y: pi.event.clientY };
            return;
          }
          const dy = pi.event.clientY - lastPointerPosRef.current.y;
          const ROT_SPEED = 0.002;
          // egér fel = előre billen
          root.rotation.x += dy * ROT_SPEED;
          lastPointerPosRef.current.y = pi.event.clientY;
          const label = sizeLabelsRef.current[oid];
          if (label) updateSizeLabelPosition(root, label);
        }
        if (dragModeRef.current === "vertical") {
          if (!lastPointerPosRef.current) {
            lastPointerPosRef.current = { y: pi.event.clientY };
            return;
          }

          const dy = pi.event.clientY - lastPointerPosRef.current.y;
          const VERT_SPEED = 0.002;
          const deltaY = -dy * VERT_SPEED;

          const oid = selectedObjectIdRef.current;
          const root = groupRootsRef.current[oid];
          if (!root) return;

          root.position.y += deltaY;

          root.computeWorldMatrix(true);

          const bi = getCombinedBoundingInfo(root);
          if (!bi) return;

          const bounds = getRoomBounds();
          const roomH = bounds.h;

          // padló
          if (bi.minimum.y < 0) {
            root.position.y -= bi.minimum.y;
          }

          // plafon
          if (bi.maximum.y > roomH) {
            root.position.y -= bi.maximum.y - roomH;
          }

          lastPointerPosRef.current.y = pi.event.clientY;
        }
      }
      if (pi.type === PointerEventTypes.POINTERUP) {
        if (!isDraggingRef.current || !dragModeRef.current) {
          dragModeRef.current = null;
          return;
        }
        if (!pointerMovedRef.current) {
          const oid = selectedObjectIdRef.current;
          const root = groupRootsRef.current[oid];
          if (root) {
            // collisionOnDropRef.current = hasCollision3D(root, oid);
          }
          if (!root) return;
          if (dragModeRef.current === "rotate-y") {
            const deg = Tools.ToDegrees(root.rotation.y);
            const next = (Math.round(deg / 90) * 90 + 90) % 360;
            root.rotation.y = Tools.ToRadians(next);
            const label = sizeLabelsRef.current[oid];
            if (label) updateSizeLabelPosition(root, label);
          }
          if (dragModeRef.current === "rotate-x") {
            const deg = Tools.ToDegrees(root.rotation.x);
            const next = (Math.round(deg / 90) * 90 + 90) % 360;
            root.rotation.x = Tools.ToRadians(next);
            const label = sizeLabelsRef.current[oid];
            if (label) updateSizeLabelPosition(root, label);
          }
        }
        if (isSnappingEnabledRef.current && dragModeRef.current === "move") {
          const oid = selectedObjectIdRef.current;
          const root = groupRootsRef.current[oid];

          if (root) {
            // 🔒 mentsük az eredeti pozíciót
            const originalPos = root.position.clone();

            // SNAP
            snapOnPointerUp(root, oid);
            snapToRoomBounds(root);

            root.computeWorldMatrix(true);

            // 🛑 ÜTKÖZÉS ELLENŐRZÉS
            if (hasCollision3D(root, oid)) {
              // 🔥 visszaállítjuk
              root.position.copyFrom(originalPos);
              root.computeWorldMatrix(true);

              // opcionális: piros él
              setEdgesForRoot(root, "collision");
            }
          }
        }
        isDraggingRef.current = false;
        dragPlanePointRef.current = null;
        dragStartRootPosRef.current = null;
        sceneOwnsTransformRef.current = false;
        snapLockRef.current.x = null;
        snapLockRef.current.z = null;
        dragModeRef.current = null;
        lastPointerPosRef.current = null;
        const oid = selectedObjectIdRef.current;
        const root = groupRootsRef.current[oid];
        if (root) {
          setEdgesForRoot(root, "idle");
        }
        if (collisionOnDropRef.current) {
          console.warn("Collision detected");
        }
        collisionOnDropRef.current = false;
        const obj = objects.find((o) => o.objectId === oid);
        if (root && obj) {
          const t = serializeRootTransform(root);
          setObjPosition({
            x: root.position.x * 1000,
            y: root.position.y * 1000,
            z: root.position.z * 1000
          });
          setObjRotation({
            x: Math.round(Tools.ToDegrees(root.rotation.x)),
            y: Math.round(Tools.ToDegrees(root.rotation.y)),
            z: Math.round(Tools.ToDegrees(root.rotation.z))
          });
          const updatedObject = {
            ...obj,
            position: `[${t.position.join(",")}]`,
            rotation: `[${t.rotation.join(",")}]`
          };
          const idx = objects.findIndex((o) => o.objectId === oid);
          onObjectUpdate?.(updatedObject, idx);
        }
        camera.attachControl(canvasRef.current, true);
      }
    });
    engine.runRenderLoop(() => scene.render());
    // ha a window mérete változik, igazítjuk a vászont
    const onWindowResize = () => engine.resize();
    window.addEventListener("resize", onWindowResize);
    // figyeljük a container méretének változását is
    let rafId = null;
    const resizeObserver = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => engine.resize());
    });
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }
    return () => {
      if (beforeRenderObserverRef.current) {
        scene.onBeforeRenderObservable.remove(beforeRenderObserverRef.current);
        beforeRenderObserverRef.current = null;
      }
      shadowGeneratorRef.current?.dispose();
      shadowGeneratorRef.current = null;
      ceilingLightRef.current = null;
      roomMaterialsRef.current.forEach((m) => m?.dispose());
      roomMaterialsRef.current = [];
      materialCacheRef.current.forEach((m) => m.dispose());
      textureCacheRef.current.forEach((t) => t.dispose());
      materialCacheRef.current.clear();
      textureCacheRef.current.clear();
      engine.dispose();
      if (rafId) cancelAnimationFrame(rafId);
      scene.onPointerObservable.remove(pointerObserver1);
      scene.onPointerObservable.remove(pointerObserver2);
      window.removeEventListener("resize", onWindowResize);
      resizeObserver.disconnect();
    };
  }, []);
  // 2) build room, groups & items
  useEffect(() => {
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    if (!scene || !camera) return;

    // 🔥 CLEANUP: töröljük az összes size labelt (különben bent maradnak törölt objecteknél)
    Object.values(sizeLabelsRef.current).forEach((label) => {
      if (!label) return;

      if (label.material?.diffuseTexture) {
        label.material.diffuseTexture.dispose();
      }

      label.material?.dispose();
      label.dispose();
    });

    sizeLabelsRef.current = {};

    const preservedRootTransforms = Object.fromEntries(
      Object.entries(groupRootsRef.current).map(([oid, root]) => [
        oid,
        {
          position: root.position.clone(),
          rotation: root.rotation.clone()
        }
      ])
    );
    if (beforeRenderObserverRef.current) {
      scene.onBeforeRenderObservable.remove(beforeRenderObserverRef.current);
      beforeRenderObserverRef.current = null;
    }

    scene.meshes
      .filter(
        (m) =>
          m.name.startsWith("box_") ||
          m.name.startsWith("grp_") ||
          m.name.startsWith("room_")
      )
      .forEach((m) => {
        m.dispose();
      });

    // A room újraépülhet sokszor; a külön falmaterialokat is takarítsuk.
    roomMaterialsRef.current.forEach((m) => m?.dispose());
    roomMaterialsRef.current = [];

    const shadowMap = shadowGeneratorRef.current?.getShadowMap();
    if (shadowMap) shadowMap.renderList = [];

    groupRootsRef.current = {};
    wallsRef.current = {};
    const MM_TO_M = 0.001;
    const { h: roomH_mm, w: roomW_mm, d: roomD_mm } = roomSize;
    const roomH = roomH_mm * MM_TO_M;
    const roomW = roomW_mm * MM_TO_M;
    const roomD = roomD_mm * MM_TO_M;

    // A mennyezeti lámpa a szobaméret változásakor is maradjon pontosan középen.
    if (ceilingLightRef.current) {
      ceilingLightRef.current.position.set(
        roomW / 2,
        Math.max(0.02, roomH - 0.03),
        roomD / 2
      );
      ceilingLightRef.current.direction.set(0, -1, 0);
      ceilingLightRef.current.range = Math.max(roomW, roomH, roomD) * 2.5;
    }

    const thin = 0.01;
    // floor
    const floor = MeshBuilder.CreateBox(
      "room_floor",
      { width: roomW, height: thin, depth: roomD },
      scene
    );
    roomFloorRef.current = floor;
    floor.position.set(roomW / 2, thin / 2, roomD / 2);
    const floorMat = new StandardMaterial("floorMat", scene);
    floorMat.diffuseColor = new Color3(0.68, 0.68, 0.68);
    floorMat.specularColor = new Color3(0.02, 0.02, 0.02);
    floor.material = floorMat;
    floor.receiveShadows = true;
    roomMaterialsRef.current.push(floorMat);

    // walls & ceiling
    // FONTOS: minden fal külön materialt kap. A korábbi közös wallMat miatt
    // egy fal alpha módosítása az összes többi falat is módosította.
    const createWall = (name, opts, pos) => {
      const visual = MeshBuilder.CreateBox(name, opts, scene);
      visual.position.set(...pos);

      const wallMat = new StandardMaterial(`${name}_mat`, scene);
      wallMat.diffuseColor = new Color3(0.96, 0.96, 0.96);
      wallMat.alpha = 1;
      wallMat.specularColor = new Color3(0, 0, 0);
      wallMat.backFaceCulling = true;
      wallMat.transparencyMode = Material.MATERIAL_OPAQUE;
      roomMaterialsRef.current.push(wallMat);

      visual.material = wallMat;
      visual.isPickable = false; // ⛔ csak látvány
      visual.receiveShadows = true;
      wallsRef.current[name] = visual;
      // 2️⃣ láthatatlan pick fal
      const pickWall = MeshBuilder.CreateBox(`${name}_pick`, opts, scene);
      pickWall.position.set(...pos);
      pickWall.isVisible = false; // 👻 nem látszik
      pickWall.isPickable = true; // 🎯 de mérhető
      pickWall.checkCollisions = false;
    };
    createWall("room_leftWall", { width: thin, height: roomH, depth: roomD }, [
      thin / 2,
      roomH / 2,
      roomD / 2
    ]);
    createWall("room_rightWall", { width: thin, height: roomH, depth: roomD }, [
      roomW - thin / 2,
      roomH / 2,
      roomD / 2
    ]);
    createWall("room_frontWall", { width: roomW, height: roomH, depth: thin }, [
      roomW / 2,
      roomH / 2,
      thin / 2
    ]);
    createWall("room_backWall", { width: roomW, height: roomH, depth: thin }, [
      roomW / 2,
      roomH / 2,
      roomD - thin / 2
    ]);
    createWall("room_ceiling", { width: roomW, height: thin, depth: roomD }, [
      roomW / 2,
      roomH - thin / 2,
      roomD / 2
    ]);
    beforeRenderObserverRef.current = scene.onBeforeRenderObservable.add(() => {
      const camPos = camera.position;
      const center = new Vector3(roomW / 2, roomH / 2, roomD / 2);
      const dir = camPos.subtract(center);

      // Ne áttetsző falakat rajzoljunk egymásra: ez depth-sort vibrálást és
      // nagy szürke foltokat okozhat. A kamera felőli fal egyszerűen nem renderelődik.
      Object.values(wallsRef.current).forEach((w) => w?.setEnabled(true));

      if (dir.x < 0) wallsRef.current.room_leftWall?.setEnabled(false);
      else wallsRef.current.room_rightWall?.setEnabled(false);

      if (dir.z < 0) wallsRef.current.room_frontWall?.setEnabled(false);
      else wallsRef.current.room_backWall?.setEnabled(false);

      if (dir.y > 0) wallsRef.current.room_ceiling?.setEnabled(false);
    });
    if (createdItems.length === 0) {
      if (!hasPositionedCameraRef.current) {
        camera.setTarget(new Vector3(roomW / 2, roomH / 2, roomD / 2));
        camera.radius = 10;
        hasPositionedCameraRef.current = true;
      }
      return;
    }
    // compute scale for items
    const objectScale = 0.001; //Math.min(roomW, roomD, roomH) / (maxDim * 3);
    // group roots
    createdItems.forEach((item) => {
      const oid = item.object.objectId;
      // ⛔ system object → kihagyjuk
      if (systemObjectIds.current.has(oid)) return;
      let root = groupRootsRef.current[oid];
      if (!root) {
        root = new TransformNode(`grp_${oid}`, scene);
        groupRootsRef.current[oid] = root;
      }
      if (!root) {
        root = new TransformNode(`grp_${oid}`, scene);
        groupRootsRef.current[oid] = root;
      }
      // Mindig frissítsd a root pozícióját és rotációját:
      const preserved = preservedRootTransforms[String(oid)];
      if (preserved) {
        root.position.copyFrom(preserved.position);
        root.rotation.copyFrom(preserved.rotation);
      } else {
        const objData = objects.find((o) => o.objectId === oid) || {};
        const [opx, opy, opz] = parsePositionString(
          objData.position || "[0,0,0]",
          1
        )[0];
        const [orx, ory, orz] = parseRotationString(
          objData.rotation || "[0,0,0]",
          1
        )[0];
        root.position.set(
          Math.max(0, Math.min(opx, roomW)),
          Math.max(0, Math.min(opy, roomH)),
          Math.max(0, Math.min(opz, roomD))
        );
        root.rotation.set(
          Tools.ToRadians(orx),
          Tools.ToRadians(ory),
          Tools.ToRadians(orz)
        );
      }
    });
    itemMeshesRef.current = {}; // 🔑 csak itt reset
    // instantiate items
    createdItems.forEach((item, gi) => {
      const oid = item.object.objectId;
      if (systemObjectIds.current.has(oid)) return;
      const root = groupRootsRef.current[oid];
      if (!root) return;
      const rawPos = parseBracketList(item.position, item.qty);
      const rawRot = parseBracketList(item.rotation, item.qty);
      const [rw, rh, rd] = parseSizeString(item.size);
      const w = rw * objectScale,
        h = rh * objectScale,
        d = rd * objectScale;
      rawPos.forEach(([x0, y0, z0], idx) => {
        const name = `box_${oid}_${gi}_${idx}`;
        const mesh = MeshBuilder.CreateBox(
          name,
          { width: w, height: h, depth: d },
          scene
        );
        if (!itemMeshesRef.current[item.itemId]) {
          itemMeshesRef.current[item.itemId] = [];
        }
        itemMeshesRef.current[item.itemId].push(mesh);
        mesh.parent = root;
        mesh.setPivotPoint(new Vector3(-w / 2, -h / 2, -d / 2));
        mesh.position = new Vector3(
          x0 * objectScale + w / 2,
          y0 * objectScale + h / 2,
          z0 * objectScale + d / 2
        );
        const [rx, ry, rz] = rawRot[idx] || [0, 0, 0];
        mesh.rotation = new Vector3(
          (rx * Math.PI) / 2,
          (ry * Math.PI) / 2,
          (rz * Math.PI) / 2
        );
        mesh.enableEdgesRendering();
        mesh.edgesColor = new Color4(0, 0, 0, 1);
        mesh.edgesWidth = baseEdgeWidthRef.current;
        mesh.metadata = {
          ...(mesh.metadata || {}),
          edgeBaseColor: mesh.edgesColor.clone(),
          edgeBaseW: baseEdgeWidthRef.current
        };
        const mat = new StandardMaterial(`mat_${name}`, scene);
        mat.diffuseColor = new Color3(0.7, 0.7, 0.7); // default szürke

        const col = usedColors.find((c) => c.colorId === item.color?.colorId);
        mesh.material = getCachedMaterial(scene, col);
        mesh.isVisible = !hiddenItems.has(item.itemId);
        mesh.receiveShadows = true;
        shadowGeneratorRef.current?.addShadowCaster(mesh, true);
        mesh.renderOutline = false;
        mesh.outlineWidth = 0;
        mesh.outlineColor = Color3.Green();
        mesh.actionManager = new ActionManager(scene);
        millingDisplay.attach(mesh, item, {
          w,
          h,
          d,
          widthMm: rh,
          heightMm: rw,
          swapAxes: true
        });
      });
    });
    // ───────── SIZE LABELS ─────────
    Object.entries(groupRootsRef.current).forEach(([oid, root]) => {
      if (sizeLabelsRef.current[oid]) {
        sizeLabelsRef.current[oid].dispose();
      }

      root.computeWorldMatrix(true);
      const bi = getCombinedBoundingInfo(root);
      if (!bi) return;

      const width = (bi.maximum.x - bi.minimum.x) * 1000;
      const height = (bi.maximum.y - bi.minimum.y) * 1000;
      const depth = (bi.maximum.z - bi.minimum.z) * 1000;

      const sizeText =
        `${Math.round(width)} × ` +
        `${Math.round(height)} × ` +
        `${Math.round(depth)} mm`;

      const label = createSizeLabel(scene, root, sizeText);

      label.parent = root;

      sizeLabelsRef.current[oid] = label;
      const isHidden = hiddenObjectIdsRef.current.has(String(oid));
      root.setEnabled(!isHidden);
      label.setEnabled(!isHidden && showSizeLabelsRef.current);

      const size = bi.maximum.subtract(bi.minimum);

      let sizeX = size.x;
      let sizeZ = size.z;
      let sizeY = size.y;

      // root rotáció alapján korrigáljuk
      const rotIndex = Math.round(root.rotation.y / (Math.PI / 2)) & 3;
      if (rotIndex === 0) {
        sizeX = size.x / 2;
        sizeZ = size.z;
        sizeY = size.y / 2;
      }
      if (rotIndex === 1) {
        sizeX = sizeX / 1;
        sizeZ = sizeZ / -2;
        sizeY = sizeY / 2;
      }
      if (rotIndex === 2) {
        sizeX = sizeX / -2;
        sizeZ = sizeZ / -1;
        sizeY = sizeY / 2;
      }
      if (rotIndex === 3) {
        sizeX = sizeX / -1;
        sizeZ = sizeZ / 2;
        sizeY = sizeY / 2;
      }
      label.position.set(sizeX, sizeY, sizeZ);
      label.renderingGroupId = 1;
      //  label.material.depthFunction = 0;
      label.material.disableDepthWrite = true;
    });
  }, [createdItems, roomSize]);

  function getRoomBounds() {
    const floor = roomFloorRef.current;
    if (!floor) return null;

    const bi = floor.getBoundingInfo();
    const size = bi.boundingBox.maximum.subtract(bi.boundingBox.minimum);

    return {
      w: size.x,
      d: size.z,
      h: roomSize.h * 0.001
    };
  }
  useEffect(() => {
    if (sceneOwnsTransformRef.current) return;
    if (!selectedObjectIdRef.current) return;
    const oid = selectedObjectIdRef.current;
    if (!oid) return;
    const root = groupRootsRef.current[oid];
    if (!root) return;
    root.position.set(
      objPosition.x / 1000,
      objPosition.y / 1000,
      objPosition.z / 1000
    );
  }, [objPosition, selectedObjectId]);
  useEffect(() => {
    if (sceneOwnsTransformRef.current) return;
    const oid = selectedObjectIdRef.current;
    if (!oid) return;
    const root = groupRootsRef.current[oid];
    if (!root) return;
    root.rotation.set(
      Tools.ToRadians(objRotation.x),
      Tools.ToRadians(objRotation.y),
      Tools.ToRadians(objRotation.z)
    );
  }, [objRotation, selectedObjectId]);

  useEffect(() => {
    if (!isModalOpen && selectedObjectId != null) {
      // visszaállítás után töröljük a kijelölést
      const root = groupRootsRef.current[selectedObjectId];
      if (root) {
        root.getChildMeshes().forEach((m) => (m.renderOutline = false));
      }
      setSelectedObjectId(null);
    }
  }, [isModalOpen, selectedObjectId]);
  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        showRoomInfo &&
        roomInfoRef.current &&
        !roomInfoRef.current.contains(e.target)
      ) {
        setShowRoomInfo(false);
      }
    };
    window.addEventListener("mousedown", onClickOutside);
    return () => window.removeEventListener("mousedown", onClickOutside);
  }, [showRoomInfo]);

  const dimLabels = {
    h: t("height"),
    w: t("width"),
    d: t("depth")
  };
  return (
    <div
      ref={containerRef}
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      <FloatingToolbar
        showMilling3D={millingDisplay.show3D}
        onToggleMilling3D={millingDisplay.toggle}
        millingStatus={millingDisplay.status}
        onRetryMilling={millingDisplay.retry}
        containerRef={containerRef}
        measureMode={measureMode}
        onToggleMeasure={() => {
          clearMeasurement(sceneRef.current);
          setFirstPick(null);
          setMeasureMode((p) => !p);
        }}
        snappingEnabled={isSnappingEnabled}
        onToggleSnapping={() => setIsSnappingEnabled((p) => !p)}
        controlMode={controlMode}
        onToggleControlMode={() =>
          setControlMode((p) => (p === "camera" ? "object" : "camera"))
        }
        transformMode={transformMode}
        onToggleTransformMode={() =>
          setTransformMode((p) => (p === "move" ? "rotate" : "move"))
        }
        showSizeLabels={showSizeLabels}
        onToggleSizeLabels={() => setShowSizeLabels((p) => !p)}
      />
      <div
        ref={roomInfoRef}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 20
        }}
      >
        {!showRoomInfo && (
          <div
            style={{
              width: 200,
              borderRadius: 15,
              overflow: "hidden"
            }}
          >
            {/* FEJLÉC */}
            <div
              style={{
                padding: "0.6rem 0.75rem",
                position: "relative",
                height: 40
              }}
            >
              <span
                style={{
                  fontSize: "0.9rem",
                  fontWeight: "bold"
                }}
              >
                {" "}
              </span>
              {/* BEZÁRÓ IKON */}
              <div
                onClick={() => setShowRoomInfo(true)}
                style={{
                  position: "absolute",
                  top: 4,
                  right: 3,
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "#ffffff",
                  border: "1px solid #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                }}
              >
                ⓘ
              </div>
            </div>
          </div>
        )}
        {showRoomInfo && (
          <div
            style={{
              width: 200,
              borderRadius: 18,
              overflow: "hidden",
              background: "rgba(255,255,255,0.95)",
              border: "1px solid #ccc",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}
          >
            {/* FEJLÉC */}
            <div
              style={{
                padding: "0.6rem 0.75rem",
                position: "relative",
                borderBottom: "1px dashed #ddd",
                paddingBottom: "0.2rem"
              }}
            >
              <span
                style={{
                  fontSize: "0.9rem",
                  fontWeight: "bold"
                }}
              >
                {t("info")}
              </span>
              {/* BEZÁRÓ IKON */}
              <div
                onClick={() => setShowRoomInfo(false)}
                style={{
                  position: "absolute",
                  top: 3,
                  right: 3,
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "#ffffff",
                  border: "1px solid #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                }}
              >
                ⓘ
              </div>
            </div>
            <div
              style={{
                padding: "0.6rem 0.75rem",
                paddingTop: "0.2rem",
                fontSize: "0.75rem"
              }}
            >
              <RoomSizeSection
                roomSize={roomSize}
                onRoomSizeChange={onRoomSizeChange}
                dimLabels={dimLabels}
              />
              <ShortcutList />
              {activeInfoMode === "measure" && <MeasureInfo />}
              {activeInfoMode === "move" && <MoveInfo />}
              {activeInfoMode === "rotate" && <RotateInfo />}
              <SnapInfo />
            </div>
          </div>
        )}
      </div>
      <canvas
        ref={canvasRef}
        onContextMenu={(e) => e.preventDefault()}
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </div>
  );
}
// helper functions
function parseSizeString(input) {
  if (Array.isArray(input)) {
    const nums = input.map((n) => parseFloat(n) || 0);
    return nums.length === 3 ? nums : [1, 1, 1];
  }
  if (typeof input !== "string") return [1, 1, 1];
  return input
    .slice(1, -1)
    .split(",")
    .map((n) => parseFloat(n) || 0);
}
function parseBracketList(input = "", qty) {
  if (Array.isArray(input)) {
    return input
      .slice(0, qty)
      .map((a) =>
        Array.isArray(a) ? a.map((n) => parseFloat(n) || 0) : [0, 0, 0]
      );
  }
  const matches = (input.match(/\[.*?\]/g) || []).slice(0, qty);
  return Array.from({ length: qty }, (_, i) => {
    const m = matches[i];
    return m
      ? m
          .slice(1, -1)
          .split(",")
          .map((n) => parseFloat(n) || 0)
      : [0, 0, 0];
  });
}
function parsePositionString(input = "", qty) {
  if (!input.trim()) return Array(qty).fill([0, 0, 0]);
  const matches = input.match(/\[.*?\]/g) || [];
  return matches.slice(0, qty).map((m) =>
    m
      .slice(1, -1)
      .split(",")
      .map((n) => parseFloat(n.trim()) || 0)
  );
}
function parseRotationString(input = "", qty) {
  if (!input.trim()) return Array(qty).fill([0, 0, 0]);
  const matches = input.match(/\[.*?\]/g) || [];
  return matches.slice(0, qty).map((m) =>
    m
      .slice(1, -1)
      .split(",")
      .map((n) => parseFloat(n.trim()) || 0)
  );
}
export default memo(ModelViewer);
